// Global
import React from 'react';

// Local
import { CarouselStatic, CarouselStaticTrack } from './CarouselStatic';
import { PlaceholderWrapper } from 'components/helpers/SitecoreWrappers/PlaceholderWrapper/PlaceholderWrapper';
import { Themes } from 'lib/themes';
import { Item } from '@sitecore-content-sdk/nextjs';
import { usePreambleFields } from 'components/helpers/Molecules/Preamble/Preamble.sitecore';
import useIsEditing from 'lib/hooks/sitecore/useIsEditing';
import { EditingHelpText } from 'components/helpers/Editing/EditingHelpText';
import { CardFields, fieldTypes } from '../CardListingShared';
import { PreambleOrientation } from 'components/helpers/Molecules/Preamble/Preamble';
import { ContentGridParams } from '../ContentGrid/ContentGrid.sitecore';
import { Base } from 'src/.generated/Foundation.GMI.model';
import { ComponentProps } from 'lib/component-props';
import { useDefaultCardDetailLevel } from 'lib/hooks/useSiteSettings';

type CarouselStaticSitecoreProps = ComponentProps & {
  fields?: Base.ComponentFields.BasePreamble['fields'];
  params?: ContentGridParams & {
    DynamicPlaceholderId: string;
  };
};

const CarouselStaticContent = (props: CarouselStaticSitecoreProps) => {
  const { DynamicPlaceholderId } = props?.params || {};
  const phKey = DynamicPlaceholderId
    ? `custom-headless-grid-${DynamicPlaceholderId}`
    : 'custom-headless-grid';

  // Get the default card detail level from site settings (handles both flat and nested shapes)
  const defaultCardDetailLevel = useDefaultCardDetailLevel();

  // Priority: 1) cardDetails from component params, 2) defaultCardDetailLevel from site settings, 3) all fields
  const fieldsToShow = props.params?.cardDetails?.fieldsToShow?.length
    ? props.params.cardDetails.fieldsToShow.map((x: Item) => x.name as CardFields)
    : defaultCardDetailLevel?.length
      ? defaultCardDetailLevel.map((x) => x.name as CardFields)
      : [...fieldTypes];

  const preambleFields = usePreambleFields({
    fields: props.fields,
    ctaComponentClassOverrides: {
      cta1: 'component-content-grid-color-button-color-1',
      cta2: 'component-content-grid-color-button-color-2',
    },
  });

  const isEditing = useIsEditing();
  const preambleOrientation = props.params?.preambleOrientation as PreambleOrientation;

  // The Preamble is rendered once by CarouselStatic (outside the placeholder). The
  // placeholder renders only the track; nesting the Preamble inside the render
  // callback duplicates the headline/preamble markup in the editing chrome.
  const result = (
    <CarouselStatic
      {...preambleFields}
      theme={props.params?.theme as Themes}
      preambleOrientation={preambleOrientation}
      content={
        <PlaceholderWrapper
          name={phKey}
          rendering={props.rendering}
          render={(components) => (
            <CarouselStaticTrack
              // IMPORTANT: pass a *copy* of the components array.
              // In editing mode PlaceholderWrapper's fixedRender mutates the original
              // array in place (splice) right after this render() runs, replacing its
              // contents with the element we return here. Since CarouselStaticTrack maps
              // over `slides` lazily (on its own render, after the splice), holding the
              // live reference would make it render itself as its own child -> infinite
              // recursion / stack overflow. A snapshot copy avoids that.
              slides={[...components]}
              preambleAlignment={preambleOrientation}
              isEditing={isEditing}
            />
          )}
        />
      }
    />
  );

  if (isEditing) {
    const hiddenFields = fieldTypes.filter((field) => !fieldsToShow.includes(field));
    const cardDetailsSource = props.params?.cardDetails?.fieldsToShow?.length
      ? 'Card Details (Component Level)'
      : defaultCardDetailLevel?.length
        ? 'Default Card Detail Level (Site Settings)'
        : 'All Fields (Default Fallback)';
    return (
      <div>
        <EditingHelpText>Card configuration source: {cardDetailsSource}</EditingHelpText>
        <EditingHelpText>Shown fields: {fieldsToShow.join(', ')}</EditingHelpText>
        <EditingHelpText>Hidden fields: {hiddenFields.join(', ')}</EditingHelpText>
        {result}
      </div>
    );
  }

  return result;
};

export const Default = (props: CarouselStaticSitecoreProps) => {
  return <CarouselStaticContent {...props} />;
};
